<script lang="ts" setup>
const props = withDefaults(
  defineProps<{
    modelValue: string | number | null;
    type?: string;
    autocomplete?: string;
    size?: 'small' | 'normal';
    placeholder?: string;
    bgColor?: 'grey' | 'white';
    label?: string;
    name?: string;
    dataTestid?: string;
    rules?: ((val: string) => string | boolean)[];
  }>(),
  {
    type: 'text',
    size: 'small',
    bgColor: 'grey',
    label: '',
    name: undefined,
    placeholder: undefined,
    autocomplete: undefined,
    rules: undefined,
    dataTestid: undefined,
  },
);

const emit = defineEmits<{
  (event: 'update:modelValue', value: string | number | null): void;
  (event: 'focus'): void;
  (event: 'blur'): void;
}>();

const isFocused = ref<boolean>(false);
const isValidationMsg = ref<string>('');
const inputRef = ref<HTMLInputElement>();
const slots = useSlots();
const isAppendSlot = !!slots.append;

const isAutofilled = ref<boolean>(false);

const checkAutofill = function checkAutofill(e: AnimationEvent) {
  if (e.animationName === 'onAutoFillStart') {
    isAutofilled.value = true;
  } else if (e.animationName === 'onAutoFillCancel') {
    isAutofilled.value = false;
  }
};

const setIsFocused = function setIsFocused(state: boolean) {
  isFocused.value = state;

  if (state) {
    emit('focus');
  } else {
    emit('blur');
  }
};

const getError = function getError(value: string): string {
  const errMsgArr = props.rules
    ?.map((rule) => rule(value))
    .filter((res) => typeof res === 'string');

  return errMsgArr?.[0]?.toString() ?? '';
};

const inputHandler = function handleInputInput(e: Event) {
  isAutofilled.value = false;

  const { value } = e.target as HTMLInputElement;

  isValidationMsg.value = getError(value);

  emit('update:modelValue', value);
};

const validate = function validateInputValue(): boolean {
  if (!inputRef.value) return true;

  const firstError = getError(inputRef.value.value);

  isValidationMsg.value = firstError;

  return !firstError;
};

defineExpose({
  inputRef,
  validate,
});
</script>

<template>
  <label
    class="custom-input"
    :class="[
      `custom-input--${props.size}`,
      `custom-input--${props.bgColor}`,
      {
        'custom-input--labeled': props.label,
        'custom-input--filled': props.modelValue || isAutofilled,
        'custom-input--appended': isAppendSlot,
        'custom-input--error': isValidationMsg,
        'custom-input--focused': isFocused,
      },
    ]"
  >
    <div class="custom-input__wrap">
      <input
        ref="inputRef"
        class="custom-input__input text-body-s"
        :type="props.type"
        :name="props.name"
        :placeholder="props.placeholder"
        :autocomplete="props.autocomplete"
        :value="props.modelValue"
        :data-testid="props.dataTestid"
        @input="inputHandler"
        @focus="setIsFocused(true)"
        @blur="setIsFocused(false)"
        @animationstart="checkAutofill"
      />

      <span
        v-if="props.label"
        class="custom-input__label text-body-s"
      >
        {{ props.label }}
      </span>

      <div
        v-if="isAppendSlot"
        class="custom-input__append"
      >
        <slot name="append"></slot>
      </div>
    </div>

    <div
      v-if="isValidationMsg"
      class="custom-input__bottom text-body-sc"
    >
      {{ isValidationMsg }}
    </div>
  </label>
</template>

<style lang="scss">
.custom-input {
  $this: 'custom-input';

  position: relative;

  &--small {
    .#{$this}__input {
      height: 48px;
    }

    .#{$this}__label {
      top: 12px;
    }

    .#{$this}__append {
      top: 12px;
    }
  }

  &--labeled {
    .#{$this}__input {
      padding: 24px 15px 8px;
    }
  }

  &--appended {
    .#{$this}__input {
      padding: 24px 52px 8px 15px;
    }
  }

  &--filled {
    .#{$this}__label {
      transform: translateY(-10px) scale(0.8);
    }
  }

  &--white {
    .#{$this}__input {
      border-color: $color-dark-blue-300;
      background-color: $color-white;

      &:hover {
        background-color: $color-white;
      }
    }
  }

  &--error {
    .#{$this}__input {
      background-color: $color-red-100;
      border-color: $color-red-500;

      &:hover {
        background-color: $color-red-100;
        border-color: $color-red-500;
      }

      &:focus {
        background-color: $color-white;
        border-color: $color-red-500;
      }
    }

    .#{$this}__label {
      color: $color-red-500;
    }
  }

  &__label {
    pointer-events: none;
    white-space: nowrap;
    color: $color-new-black-500;
    position: absolute;
    z-index: 1;
    font-size: 13px;
    top: 15px;
    left: 15px;
    transform-origin: 0 0;
    transition:
      transform 0.3s ease,
      color 0.3s ease;

    @include media('sm') {
      font-size: 14px;
    }
  }

  &__bottom {
    color: $color-red-500;
    padding-top: 8px;
  }

  &__append {
    position: absolute;
    z-index: 1;
    right: 15px;
    top: 16px;
  }

  &__input {
    outline: none;
    width: 100%;
    height: 56px;
    padding: 6px 15px;
    border-radius: $brd-12;
    border-width: 1px;
    border-style: solid;
    border-color: $color-dark-blue-100;
    background-color: $color-dark-blue-100;
    transition:
      border-color 0.3s ease,
      background-color 0.3s ease;

    @media (hover: hover) {
      &:hover {
        border-color: $color-dark-blue-600;
        background-color: $color-dark-blue-100;
      }
    }

    &:focus {
      background-color: $color-white;
      border-color: $color-dark-blue-600;

      & + .#{$this}__label {
        transform: translateY(-10px) scale(0.8);
      }
    }
  }
}

input:-webkit-autofill {
  animation-name: onAutoFillStart;
}

input:not(:-webkit-autofill) {
  animation-name: onAutoFillCancel;
}

/* stylelint-disable-next-line keyframes-name-pattern */
@keyframes onAutoFillStart {
  /* stylelint-disable-next-line block-no-empty */
  from {
  }

  /* stylelint-disable-next-line block-no-empty */
  to {
  }
}

/* stylelint-disable-next-line keyframes-name-pattern */
@keyframes onAutoFillCancel {
  /* stylelint-disable-next-line block-no-empty */
  from {
  }

  /* stylelint-disable-next-line block-no-empty */
  to {
  }
}
</style>
